import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, Circle, CircleAlert, Lock, Loader2, XCircle, ArrowUpRight } from "lucide-react";
import { api } from "@/portal/PortalAuth";
import { money } from "@/components/PriceBreakdown";

const STATE = {
  completed: { label: "Completed", Icon: CheckCircle2, cls: "text-[#4A6A48]", chip: "bg-[#DCFCE7] text-[#166534]" },
  pending: { label: "Pending", Icon: Circle, cls: "text-[#A8A29E]", chip: "bg-[#F5F5F4] text-[#57534E]" },
  action_required: { label: "Action required", Icon: CircleAlert, cls: "text-[#B45309]", chip: "bg-[#FEF3C7] text-[#92400E]" },
  needs_attention: { label: "Needs attention", Icon: CircleAlert, cls: "text-[#B45309]", chip: "bg-[#FEF3C7] text-[#92400E]" },
  rejected: { label: "Rejected", Icon: XCircle, cls: "text-[#B91C1C]", chip: "bg-[#FEE2E2] text-[#991B1B]" },
  locked: { label: "Locked", Icon: Lock, cls: "text-[#D6D3D1]", chip: "bg-[#F5F5F4] text-[#A8A29E]" },
};
const ACTION_LABEL = { sign_agreement: "Read & sign agreement", verify_id: "Verify my ID", accept_revised_total: "Review revised total", pay_now: "Pay now (Stripe)" };

export function BookingJourney() {
  const [list, setList] = useState(null);
  useEffect(() => { api.get("/guest/journey").then(({ data }) => setList(Array.isArray(data) ? data : [])).catch(() => setList([])); }, []);
  if (list === null) return <Loader2 className="w-5 h-5 animate-spin text-[#78716C]" />;
  if (!list.length) return null;
  return (
    <section className="space-y-5" data-testid="booking-journey">
      <div>
        <p className="text-[10px] uppercase tracking-[0.2em] font-medium text-[#8A9A86]">My reservation</p>
        <h2 className="mt-1 text-lg font-normal text-[#1C1917]">Booking progress</h2>
      </div>
      {list.map((r) => <JourneyCard key={r.id} r={r} />)}
    </section>
  );
}

function JourneyCard({ r }) {
  const done = r.stages.filter((s) => s.state === "completed").length;
  const act = r.current_action;
  const payNow = act?.action === "pay_now" && r.next_installment?.checkout_url;
  return (
    <div className="rounded-2xl border border-[#E7E5E4] bg-white p-6" data-testid={`journey-${r.id}`}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="text-lg text-[#1C1917]">{r.room_name} <span className="text-[#78716C] text-sm">· {r.check_in} → {r.check_out} · {r.nights} nights</span></p>
          <p className="text-xs text-[#78716C]">Reference <span data-testid={`journey-ref-${r.id}`} className="font-mono text-[#1C1917]">{r.reference || r.id.slice(0, 8)}</span> · Status <span data-testid={`journey-status-${r.id}`} className="text-[#1C1917]">{r.status}</span>{r.is_test && <span className="ml-2 rounded-full bg-[#FEF3C7] text-[#92400E] px-2 py-0.5 text-[10px] font-medium">TEST</span>}</p>
        </div>
        <div className="text-right text-sm">
          <p className="text-[#1C1917]">{money(r.estimated_total)}</p>
          <p className="text-xs text-[#78716C]">{r.amount_paid > 0 ? `${money(r.amount_paid)} paid · ${money(r.outstanding_balance)} remaining` : "estimated total"} · {done}/{r.stages.length} steps</p>
        </div>
      </div>
      {r.status === "Pending Review" && <p className="mt-3 text-xs text-[#78716C]" data-testid={`journey-note-${r.id}`}>Submitting a request does not confirm the reservation. Masaya reviews availability manually and will email you.</p>}
      <ol className="mt-5 space-y-3">
        {r.stages.map((s, i) => {
          const m = STATE[s.state] || STATE.pending;
          return (
            <li key={s.stage} className="flex gap-3" data-testid={`journey-stage-${r.id}-${i + 1}`}>
              <m.Icon className={`w-5 h-5 shrink-0 mt-0.5 ${m.cls}`} strokeWidth={1.75} />
              <div className="flex-1 min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <p className={`text-sm ${s.state === "locked" ? "text-[#A8A29E]" : "text-[#1C1917]"}`}>{i + 1}. {s.stage}</p>
                  <span data-testid={`journey-stage-state-${r.id}-${i + 1}`} className={`rounded-full px-2 py-0.5 text-[10px] font-medium ${m.chip}`}>{m.label}</span>
                </div>
                {s.detail && <p className="text-xs text-[#78716C] mt-0.5">{s.detail}</p>}
              </div>
            </li>
          );
        })}
      </ol>
      <div className="mt-5 flex flex-wrap items-center gap-3">
        {act && (payNow
          ? <a href={r.next_installment.checkout_url} data-testid={`journey-action-${r.id}`} className="inline-flex items-center gap-2 rounded-full bg-[#1C1917] text-white px-5 py-2.5 text-sm font-medium hover:bg-[#292524]">{ACTION_LABEL.pay_now} <ArrowUpRight className="w-4 h-4" /></a>
          : <Link to={r.pay_url} data-testid={`journey-action-${r.id}`} className="inline-flex items-center gap-2 rounded-full bg-[#1C1917] text-white px-5 py-2.5 text-sm font-medium hover:bg-[#292524]">{ACTION_LABEL[act.action] || "Continue"} <ArrowUpRight className="w-4 h-4" /></Link>)}
        <Link to={r.pay_url} data-testid={`journey-details-${r.id}`} className="text-sm underline underline-offset-4 text-[#1C1917]">Agreement, price details, receipts & installments</Link>
      </div>
    </div>
  );
}
